import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ActionKey,
  type DailyLedgerGroup,
  type DailyLedgerKey,
  type DailyLedgersResponse,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { formatAccountingInstant } from '../../lib/accounting-time';

const button = 'min-h-11 border border-base-300 px-3 py-2 disabled:opacity-50';
function noRecordedZipaMovements(group: DailyLedgerGroup) {
  return (
    group.key === 'ZIPA' &&
    group.total === 0 &&
    (group.warnings.includes('ZIPA_HISTORY_WITHOUT_LEDGER_ANCHOR') ||
      (group.status === 'READY' &&
        group.increases !== null &&
        group.decreases !== null &&
        Number(group.increases) === 0 &&
        Number(group.decreases) === 0))
  );
}

export function DailyLedgersPanel({
  sessionId,
  isCurrentBusinessDate = false,
}: {
  sessionId: string;
  isCurrentBusinessDate?: boolean;
}) {
  const actor = useAuthStore((s) => s.user?.id),
    token = useAuthStore((s) => s.accessToken),
    revision = useAuthStore((s) => s.sessionRevision);
  const venue = sessionVenueKey(token);
  return (
    <LedgerView
      key={`${actor}:${venue}:${revision}:${sessionId}`}
      sessionId={sessionId}
      scope={`${actor}:${venue}:${revision}`}
      venue={venue}
      isCurrentBusinessDate={isCurrentBusinessDate}
    />
  );
}
function LedgerView({
  sessionId,
  scope,
  venue,
  isCurrentBusinessDate,
}: {
  sessionId: string;
  scope: string;
  venue: string;
  isCurrentBusinessDate: boolean;
}) {
  const { t, i18n } = useTranslation('accounting');
  const canRead = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.ACCOUNTING_READ),
  );
  const canMemberRead = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.MEMBER_READ),
  );
  const [group, setGroup] = useState<DailyLedgerKey>('MEMBERSHIP');
  const [page, setPage] = useState(1),
    [memberId, setMemberId] = useState<string>();
  const cutoff = useRef<string>();
  const query = useQuery({
    queryKey: ['daily-ledgers', scope, sessionId, memberId, page],
    queryFn: async ({ signal }) => {
      const result = await api.get<DailyLedgersResponse>('/accounting/daily-ledgers', {
        signal,
        params: { sessionId, memberId, page, limit: 20, cutoff: cutoff.current },
      });
      cutoff.current = result.cutoff;
      return result;
    },
    enabled: canRead && !!venue,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });
  const data =
    !query.isError && query.data?.venueId === venue && query.data.sessionId === sessionId
      ? query.data
      : undefined;
  const refresh = () => {
    cutoff.current = undefined;
    void query.refetch();
  };
  const number = (n: string | null | undefined) =>
    n == null
      ? t('dailyLedgers.unavailable')
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (!canRead) return <p role="alert">{t('dailyLedgers.denied')}</p>;
  const selected = data?.groups.find((g) => g.key === group);
  const noMovementLabel = t(
    isCurrentBusinessDate
      ? 'dailyLedgers.zipaNoMovementsToday'
      : 'dailyLedgers.zipaNoMovementsPeriod',
  );
  return (
    <section aria-label={t('dailyLedgers.title')} className="space-y-4 border border-base-300 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('dailyLedgers.title')}</h2>
        <button type="button" className={button} disabled={query.isFetching} onClick={refresh}>
          {t('dailyLedgers.refresh')}
        </button>
      </div>
      {query.isFetching && <p role="status">{t('dailyLedgers.loading')}</p>}
      {query.isError && <p role="alert">{t('dailyLedgers.failed')}</p>}
      {data && (
        <>
          <p className="text-sm">
            {t('dailyLedgers.asOf', {
              date: data.businessDate,
              cutoff:
                formatAccountingInstant(
                  data.cutoff,
                  data.displayTimezone ?? 'Asia/Taipei',
                  i18n.language,
                ) ?? t('dailyLedgers.unavailable'),
            })}
          </p>
          <p className="text-sm text-base-content/70">{t('dailyLedgers.unitsNotice')}</p>
          {memberId && (
            <button
              type="button"
              className={button}
              onClick={() => {
                setMemberId(undefined);
                setPage(1);
              }}
            >
              {t('dailyLedgers.clearMember')}
            </button>
          )}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {data.groups.map((g) => (
              <button
                type="button"
                key={g.key}
                aria-pressed={g.key === group}
                className={`${button} text-left ${g.key === group ? 'border-primary bg-primary/10' : ''}`}
                onClick={() => {
                  setGroup(g.key);
                  setPage(1);
                }}
              >
                <span className="block font-medium">{t(`dailyLedgers.groups.${g.key}`)}</span>
                <span className="block text-xs">
                  {noRecordedZipaMovements(g)
                    ? noMovementLabel
                    : t(`dailyLedgers.status.${g.status}`)}
                </span>
                {!(noRecordedZipaMovements(g) && g.closing === null) && (
                  <span className="block tabular-nums">
                    {number(g.key === 'ZIPA' ? g.closing : (g.closing ?? g.net))}
                    {(g.key !== 'ZIPA' || g.closing !== null) && <> {g.unit}</>}
                  </span>
                )}
              </button>
            ))}
          </div>
          {selected && (
            <>
              <h3 className="font-semibold">{t(`dailyLedgers.groups.${selected.key}`)}</h3>
              {noRecordedZipaMovements(selected) && <p role="status">{noMovementLabel}</p>}
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                {(['opening', 'increases', 'decreases', 'closing'] as const).map((k) => (
                  <div key={k}>
                    <dt>{t(`dailyLedgers.${k}`)}</dt>
                    <dd className="tabular-nums">
                      {number(selected[k])}
                      {selected[k] !== null && <> {selected.unit}</>}
                    </dd>
                  </div>
                ))}
              </dl>
              {selected.warnings.map((w) => (
                <p key={w} role="status" className="text-sm">
                  {t(`dailyLedgers.warnings.${w}`, { defaultValue: t('dailyLedgers.unavailable') })}
                </p>
              ))}
              {selected.key === 'ZIPA' &&
                selected.total === 0 &&
                selected.warnings.includes('ZIPA_HISTORY_WITHOUT_LEDGER_ANCHOR') && (
                  <p role="status">{t('dailyLedgers.zipaNoRecordedMovements')}</p>
                )}
              {selected.total === 0 && selected.status !== 'UNAVAILABLE' && (
                <p role="status">{t('dailyLedgers.empty')}</p>
              )}
              <div
                className="overflow-x-auto"
                tabIndex={0}
                role="region"
                aria-label={t(`dailyLedgers.groups.${selected.key}`)}
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {[
                        'member',
                        'source',
                        'amount',
                        'balance',
                        'eventDay',
                        'postingDay',
                        'operator',
                      ].map((k) => (
                        <th key={k} className="whitespace-nowrap px-2 py-2 text-left">
                          {t(`dailyLedgers.${k}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selected.rows.map((r) => (
                      <tr
                        key={r.id}
                        data-ledger-id={r.ledgerId ?? r.sourceId}
                        className="border-t border-base-300 align-top"
                      >
                        <td className="min-w-32 px-2 py-2">
                          {r.memberId ? (
                            <>
                              {canMemberRead && r.memberHref ? (
                                <Link className="text-primary underline" to={r.memberHref}>
                                  {r.memberName ?? r.memberId}
                                </Link>
                              ) : (
                                (r.memberName ?? r.memberId)
                              )}
                              <button
                                type="button"
                                className="mt-1 block min-h-11 text-xs underline"
                                onClick={() => {
                                  setMemberId(r.memberId!);
                                  setPage(1);
                                }}
                              >
                                {t('dailyLedgers.filterMember')}
                              </button>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="min-w-48 px-2 py-2">
                          <p>{t(`dailyLedgers.kinds.${r.kind}`, { defaultValue: r.kind })}</p>
                          <code className="block break-all text-xs">{r.sourceId}</code>
                          {r.tournamentId && (
                            <Link
                              className="block text-primary underline"
                              to={`/tournament/${r.tournamentId}`}
                            >
                              {t('dailyLedgers.event')}
                            </Link>
                          )}
                          {r.correctionId && (
                            <p className="break-all text-xs">
                              {t('dailyLedgers.correction')}: {r.correctionId}
                            </p>
                          )}
                          {r.grossAward !== undefined && (
                            <p className="text-xs">
                              {t('dailyLedgers.awardSplit', {
                                gross: r.grossAward,
                                z: r.zPoints,
                                net: r.netAward,
                              })}{' '}
                              · {t(`dailyLedgers.payout.${r.payoutStatus}`)}
                            </p>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {r.amount === null ? t('dailyLedgers.pending') : number(r.amount)}{' '}
                          {r.unit}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {number(r.runningBalance)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {r.sourceBusinessDate ?? t('dailyLedgers.unattributed')}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          <p>{r.postingBusinessDate}</p>
                          <time dateTime={r.postedAt}>
                            {formatAccountingInstant(
                              r.postedAt,
                              data.displayTimezone ?? 'Asia/Taipei',
                              i18n.language,
                              { businessDate: data.businessDate },
                            ) ?? t('dailyLedgers.unavailable')}
                          </time>
                        </td>
                        <td className="max-w-48 break-words px-2 py-2 text-xs">
                          {r.actorUsername ?? t('dailyLedgers.unavailable')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className={button}
                  disabled={page === 1 || query.isFetching}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t('dailyLedgers.previous')}
                </button>
                <p>{t('dailyLedgers.page', { page, total: selected.total })}</p>
                <button
                  type="button"
                  className={button}
                  disabled={page * selected.limit >= selected.total || query.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('dailyLedgers.next')}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
